// src/pages/Digi_Insurance/BulkRegistration_Dashboard.jsx

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../Config";
import MainLayout from "../../layouts/MainLayout";
import PageLoader from "../../components/Loader/PageLoader";
import { useNavigate } from "react-router-dom";

const API_BASE = `${BASE_URL}api/Reporting`;

export default function BulkRegistration_Dashboard() {
  const today = new Date().toISOString().split("T")[0];

  const [from, setFrom]                     = useState(today);
  const [to, setTo]                         = useState(today);
  const [selectedClient, setSelectedClient] = useState("");
  const [clients, setClients]               = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [loading, setLoading]               = useState(false);
  const [data, setData]                     = useState([]);

  const navigate    = useNavigate();

  // ── StrictMode double-fire guard ─────────────────────────────────
  // React StrictMode intentionally mounts every component twice in
  // development to surface side-effect bugs.  The ref below ensures
  // the combined init effect runs only once regardless of that.
  const initialised = useRef(false);

  // ── Single init effect: clients + first summary in parallel ──────
  useEffect(() => {
    if (initialised.current) return;   // StrictMode second run — skip
    initialised.current = true;

    const init = async () => {
      // Clients list aur summary ek saath fetch karo
      setClientsLoading(true);
      setLoading(true);

      try {
        const [clientsRes, summaryRes] = await Promise.all([
          axios.get(`${API_BASE}/GetBulkJobClients`),
          axios.get(`${API_BASE}/DashboardSummary`, {
            params: { From: today, To: today },
          }),
        ]);

        if (clientsRes.data?.responseCode === "0000") {
          setClients(clientsRes.data.data || []);
        }

        if (summaryRes.data?.responseCode === "0000") {
          setData(summaryRes.data.data || []);
        } else {
          setData([]);
        }
      } catch (err) {
        console.error("Init fetch error:", err);
        setData([]);
      } finally {
        setClientsLoading(false);
        setLoading(false);
      }
    };

    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once init with StrictMode guard
  }, []); // ← sirf ek baar, mount par

  // ── Refresh / Search button handler ──────────────────────────────
  const handleRefresh = async () => {
    setLoading(true);
    setData([]);
    try {
      const params = { From: from, To: to };
      if (selectedClient) params.Client = selectedClient;

      const res = await axios.get(`${API_BASE}/DashboardSummary`, { params });

      if (res.data?.responseCode === "0000") {
        setData(res.data.data || []);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Dashboard error:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Client dropdown change (sirf state update, no fetch) ─────────
  const handleClientChange = (e) => setSelectedClient(e.target.value);

  // ── Client-side filter (backup / instant UX) ─────────────────────
  const filteredData = selectedClient
    ? data.filter(
        (d) => d.clientName?.toLowerCase() === selectedClient.toLowerCase()
      )
    : data;

  // ── KPI totals ────────────────────────────────────────────────────
  const totalClients = filteredData.length;
  const totalSuccess = filteredData.reduce((a, b) => a + (b.successUsers || 0), 0);
  const totalFailed  = filteredData.reduce((a, b) => a + (b.failedUsers  || 0), 0);
  const totalJobs    = filteredData.reduce((a, b) => a + (b.totalJobs    || 0), 0);

  // ── Client colour helper ──────────────────────────────────────────
  const getClientColor = (name = "") => {
    const n = name.toLowerCase();
    if (n.includes("zong"))    return "bg-zong text-white";
    if (n.includes("jazz"))    return "bg-jazz text-white";
    if (n.includes("ufone"))   return "bg-ufone text-white";
    if (n.includes("telenor")) return "bg-telenor text-white";
    return "bg-primary text-white";
  };

  return (
    <MainLayout>
      <div className="container-fluid">

        {/* ── HEADER ─────────────────────────────────────────────── */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h4 className="fw-bold mb-0">Dashboard Summary</h4>

          <div className="d-flex flex-wrap gap-2 align-items-center">

            {/* Client Dropdown */}
            <select
              className="form-select flex-grow-1"
              style={{ minWidth: 140, maxWidth: 220 }}
              value={selectedClient}
              onChange={handleClientChange}
              disabled={clientsLoading}
            >
              <option value="">
                {clientsLoading ? "Loading…" : "All Clients"}
              </option>
              {clients.map((c) => (
                <option key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </option>
              ))}
            </select>

            {/* Date Range */}
            <input
              type="date"
              className="form-control flex-grow-1"
              style={{ minWidth: 130, maxWidth: 180 }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <span className="text-muted d-none d-sm-inline">to</span>
            <input
              type="date"
              className="form-control flex-grow-1"
              style={{ minWidth: 130, maxWidth: 180 }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />

            <button
              className="btn btn-primary"
              onClick={handleRefresh}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>

        {/* ── KPI CARDS ──────────────────────────────────────────── */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card shadow-sm border-0 bg-primary text-white">
              <div className="card-body">
                <h3 className="mb-0">{totalClients}</h3>
                <small>Total Clients</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card shadow-sm border-0 bg-info text-white">
              <div className="card-body">
                <h3 className="mb-0">{totalJobs}</h3>
                <small>Total Jobs</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card shadow-sm border-0 bg-success text-white">
              <div className="card-body">
                <h3 className="mb-0">{totalSuccess}</h3>
                <small>Success Users</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card shadow-sm border-0 bg-danger text-white">
              <div className="card-body">
                <h3 className="mb-0">{totalFailed}</h3>
                <small>Failed Users</small>
              </div>
            </div>
          </div>
        </div>

        {/* ── LOADING ────────────────────────────────────────────── */}
        {loading && <PageLoader label="Fetching data…" />}

        {/* ── EMPTY STATE ────────────────────────────────────────── */}
        {!loading && filteredData.length === 0 && (
          <div className="text-center text-muted py-5">
            <i className="bi bi-inbox fs-1 d-block mb-2"></i>
            No data found for the selected filters.
          </div>
        )}

        {/* ── CLIENT CARDS ───────────────────────────────────────── */}
        <div className="row g-3">
          {!loading &&
            filteredData.map((client, idx) => (
              <div className="col-lg-6" key={idx}>
                <div
                  className="card shadow-sm border-0 h-100"
                  style={{ cursor: "pointer", overflow: "hidden" }}
                  onClick={() =>
                    navigate("/Pages/BulkRegistrationReport", {
                      state: { clientName: client.clientName },
                    })
                  }
                >
                  {/* Header Strip */}
                  <div className={`p-3 ${getClientColor(client.clientName)}`}>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="fw-bold mb-0 text-uppercase">
                        {client.clientName}
                      </h6>
                      <span className="badge bg-light text-dark">
                        {client.totalJobs || 0} Jobs
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="card-body bg-white">
                    <div className="row g-2">

                      <div className="col-4">
                        <div className="p-2 bg-light rounded text-center">
                          <small className="text-muted d-block">Total Users</small>
                          <h5 className="mb-0">{client.totalUsers || 0}</h5>
                        </div>
                      </div>

                      <div className="col-4">
                        <div className="p-2 bg-success bg-opacity-10 rounded text-center">
                          <small className="text-muted d-block">Success</small>
                          <h5 className="mb-0 text-success">
                            {client.successUsers || 0}
                          </h5>
                        </div>
                      </div>

                      <div className="col-4">
                        <div className="p-2 bg-danger bg-opacity-10 rounded text-center">
                          <small className="text-muted d-block">Failed</small>
                          <h5 className="mb-0 text-danger">
                            {client.failedUsers || 0}
                          </h5>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

      </div>

      {/* ── CUSTOM BRAND COLOURS ─────────────────────────────────── */}
      <style>{`
        .bg-jazz    { background-color: #221F20 !important; }
        .bg-ufone   { background-color: #F47821 !important; }
        .bg-zong    { background-color: #8CC63E !important; }
        .bg-telenor { background-color: #007AD0 !important; }
      `}</style>

    </MainLayout>
  );
}